import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Users, Vote, Building2, ChevronRight, AlertCircle, CheckCircle2, Timer, TrendingUp } from 'lucide-react';
import { ProjectCard } from './ProjectCard';
import { VotingModal } from './VotingModal';
import { NINVerification } from './NINVerification';

const CommunityPulse = () => {
  const [isVerified, setIsVerified] = useState(false);
  const [activeProjects, setActiveProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showVotingModal, setShowVotingModal] = useState(false);
  const [votingPower, setVotingPower] = useState(0);

  // Sample projects data (replace with API call)
  const projects = [
    {
      id: 1,
      title: "Gwarinpa Road Expansion",
      description: "Expansion of major roads in Gwarinpa to reduce traffic congestion",
      location: "Gwarinpa, Abuja",
      budget: "₦500M",
      timeline: "12 months",
      votesFor: 1234,
      votesAgainst: 123,
      status: "active",
      category: "transportation",
      impact: {
        population: "50,000+",
        propertyValue: "+15%",
        timeline: "2024-2025"
      }
    },
    {
      id: 2,
      title: "Jabi Lake Smart Lighting",
      description: "Installation of solar-powered smart street lights around Jabi Lake",
      location: "Jabi, Abuja",
      budget: "₦150M",
      timeline: "6 months",
      votesFor: 856,
      votesAgainst: 234,
      status: "active",
      category: "utilities",
      impact: {
        population: "25,000+",
        propertyValue: "+8%",
        timeline: "2024"
      }
    }
  ];

  useEffect(() => {
    setActiveProjects(projects);
  }, []);

  const stats = [
    {
      title: "Active Projects",
      value: activeProjects.length,
      icon: Building2,
      color: "text-blue-600"
    },
    {
      title: "Community Members",
      value: "5,234",
      icon: Users,
      color: "text-green-600"
    },
    {
      title: "Total Votes Cast",
      value: "15,678",
      icon: Vote,
      color: "text-purple-600"
    },
    {
      title: "Projects Completed",
      value: "12",
      icon: CheckCircle2,
      color: "text-emerald-600"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      {/* Hero Section */}
      <section className="bg-white border-b">
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-4xl mx-auto text-center">
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-3xl md:text-4xl font-bold mb-4"
            >
              Shape Your Community's Future
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-gray-600 mb-8"
            >
              Vote on infrastructure projects that matter to your community. 
              Verified with NIN for transparent and secure voting.
            </motion.p>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
              {stats.map((stat, index) => (
                <motion.div
                  key={stat.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 + index * 0.1 }}
                  className="bg-white p-4 rounded-lg border hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <stat.icon className={`h-5 w-5 ${stat.color}`} />
                    <span className="font-bold text-xl">{stat.value}</span>
                  </div>
                  <p className="text-sm text-gray-600">{stat.title}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Projects List - Left Column */}
          <div className="lg:col-span-2 space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold">Active Projects</h2>
              <button className="text-sm text-[#1c5bde] hover:underline">
                View All
              </button>
            </div>

            {activeProjects.map((project) => (
              <ProjectCard 
                key={project.id}
                project={project}
                onVote={() => {
                  if (!isVerified) {
                    // Show NIN verification modal
                    return;
                  }
                  setSelectedProject(project);
                  setShowVotingModal(true);
                }}
              />
            ))}
          </div>

          {/* Verification & Stats - Right Column */}
          <div className="space-y-6">
            {!isVerified ? (
              <NINVerification 
                onVerificationComplete={() => setIsVerified(true)}
              />
            ) : (
              <div className="bg-white p-6 rounded-lg border">
                <div className="flex items-center gap-2 text-green-600 mb-4">
                  <CheckCircle2 className="h-5 w-5" />
                  <span className="font-medium">Verified Voter</span>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Your voting power: {votingPower}
                </p>
                <button className="w-full px-4 py-2 bg-[#1c5bde] text-white rounded-lg hover:bg-[#0c0d8a] transition-colors">
                  View Voting History
                </button>
              </div>
            )}

            {/* Additional community stats and updates */}
          </div>
        </div>
      </section>

      {/* Voting Modal */}
      {showVotingModal && selectedProject && (
        <VotingModal
          project={selectedProject}
          onClose={() => setShowVotingModal(false)}
          onVote={(support) => {
            // Handle vote submission
            setShowVotingModal(false);
          }}
        />
      )}
    </div>
  );
};

export default CommunityPulse; 